import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { catchError, Observable, throwError } from 'rxjs';
import { B2CResponseInterface } from '../interfaces/b2c-response.interface';

@Injectable({
  providedIn: 'root',
})
export class B2cService {
  signUpUrl = `${environment.authority}${environment.authorizePath.slice(1)}?p=${environment.signUp}&client_id=${
    environment.clientID
  }&nonce=defaultNonce&redirect_uri=${environment.redirectUri}/&scope=openid&response_type=id_token&prompt=login`;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
  ) {}

  /**
   * get b2c auth data
   * @param url - url
   * @param params - query params
   */
  getB2CAuthData(url, params): Observable<B2CResponseInterface> {
    return this.http
      .post<B2CResponseInterface>(url + '?' + params, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get b2c refresh token data
   * @param refreshToken - refresh token
   */
  getB2CRefreshTokenData(refreshToken: string): Observable<B2CResponseInterface> {
    const url =
      environment.authority +
      this.getSignInPolicy() +
      `/oauth2/v2.0/token?grant_type=refresh_token&client_id=${environment.clientID}&scope=${environment.apiScope}` +
      `&refresh_token=${refreshToken}`;

    return this.http.post<B2CResponseInterface>(url, {}).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // FIXME: Optimize the code. Vaibhav Bharuka. Jan 6 11.54 pm MST
  getParams(type = 'code') {
    const code = this.cookieService.get('codeFromB2C');
    const refreshToken = this.cookieService.get('RefreshToken');
    var clientId = environment.clientID;
    var redirectUri = environment.redirectUri;
    var scope = environment.apiScope;
    var grantType: string;
    var token: string;
    var typeKey: string;
    if (type == 'code') {
      grantType = 'authorization_code';
      token = code;
      typeKey = 'code';
    } else {
      grantType = 'refresh_token';
      token = refreshToken;
      typeKey = 'refresh_token';
    }
    const payload = new HttpParams()
      .set('grant_type', grantType)
      .set('client_id', clientId)
      .set('scope', scope)
      .set(typeKey, token)
      .set('redirect_uri', redirectUri)
      .set('code_verifier', 'YTFjNjI1OWYzMzA3MTI4ZDY2Njg5M2RkNmVjNDE5YmEyZGRhOGYyM2IzNjdmZWFhMTQ1ODg3NDcxY2Nl');
    return payload;
  }

  // FIXME: Move to shared services. Vaibhav Bharuka. Jan 4 2021. 11.27 pm MST.
  getRandomString(length: number) {
    let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
    let text = '';
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  // FIXME: Optimize the code. Vaibhav Bharuka. Jan 6 11.54 pm MST
  getUrl(mode: string, responseType: string = 'code') {
    var authority = environment.authority;
    var scope = environment.apiScope;
    var state = this.getRandomString(15);
    var clientId = environment.clientID;
    var redirectUri = environment.redirectUri;
    var prompt = 'login';
    var responseMode = 'query';

    if (mode == 'signIn') {
      const signInPolicy = this.getSignInPolicy();
      this.cookieService.set('CurrentPolicy', 'SignIn');
      this.cookieService.set('CurrentPolicyName', signInPolicy);
      authority += signInPolicy;
    } else if (mode == 'SignUp') {
      this.cookieService.set('CurrentPolicy', 'SignUp');
      return (url = this.signUpUrl);
    } else if (mode == 'PasswordReset') {
      this.cookieService.set('CurrentPolicy', 'PasswordReset');
      authority += environment.resetPassword;
    } else if (mode == 'accessToken') {
      const currentPolicy = this.cookieService.get('CurrentPolicy');
      if (currentPolicy == 'SignIn') {
        // the policy pinned at the authorize step wins: the code must be exchanged against the very same policy that issued it
        const signInPolicy = this.cookieService.get('CurrentPolicyName') || this.getSignInPolicy();
        return authority + (signInPolicy + environment.tokenPath);
      } else if (currentPolicy == 'SignUp') {
        return authority + (environment.signUp + environment.tokenPath);
      } else return authority + (environment.resetPassword + environment.tokenPath);
    }

    authority += environment.authorizePath;
    this.cookieService.set('State', state);
    var url = `${authority}?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=${responseType}&prompt=${prompt}&response_mode=${responseMode}`;
    if (responseType == 'code') {
      const codeChallenge = 'YTFjNjI1OWYzMzA3MTI4ZDY2Njg5M2RkNmVjNDE5YmEyZGRhOGYyM2IzNjdmZWFhMTQ1ODg3NDcxY2Nl';
      const codeChallengeMethod = 'plain';
      url += `&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}`;
    }

    return url;
  }

  checkIsCalledFromOkta() {
    const isCalledFromOktaDashboard = localStorage.getItem('isCalledFromOktaDashboard');
    if (isCalledFromOktaDashboard == 'true') return true;
    return false;
  }

  /**
   * okta realm the user came from
   * 'trinity' is the default for the sessions created before the strata realm existed
   */
  getOktaRealm(): string {
    return localStorage.getItem('oktaRealm') == 'strata' ? 'strata' : 'trinity';
  }

  /**
   * okta org url of the current realm, decoded from the environment marker
   */
  getOktaOrgUrl(): string {
    const oktaOrg = this.getOktaRealm() == 'strata' ? environment.strataRedirectUri : environment.oktaRedirectUri;
    return decodeURIComponent(oktaOrg) + '/';
  }

  /**
   * b2c sign in policy for the current user
   * the single source of truth for both the authorize and the token requests
   */
  getSignInPolicy(): string {
    if (this.checkIsCalledFromOkta() && environment.isOktSSOEnable == true) {
      return this.getOktaRealm() == 'strata' ? environment.signInStrata : environment.signInOkta;
    }
    return environment.signIn;
  }
}
